import { useCallback, useEffect, useRef, useState } from "react";
import type {
  OperatorOverview,
  OperatorPipelineV1,
  OperatorRecapV1,
  RecapPeriod,
  SiteId,
} from "@siteagain/shared-types";

import { deriveFreshness, REFRESH_INTERVAL_MS } from "./freshness";
import { operatorFetch } from "./format";

export { deriveFreshness, REFRESH_INTERVAL_MS, STALE_AFTER_MS, type Freshness } from "./freshness";

export function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

export function useMissionControl(scope: { siteId: SiteId | null; period: RecapPeriod }) {
  const [overview, setOverview] = useState<OperatorOverview | null>(null);
  const [pipeline, setPipeline] = useState<OperatorPipelineV1 | null>(null);
  const [recap, setRecap] = useState<OperatorRecapV1 | null>(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(true);
  const [allFailed, setAllFailed] = useState(false);
  const [anyFailed, setAnyFailed] = useState(false);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const generation = useRef(0);
  const now = useNow();

  const load = useCallback(async () => {
    // A response that arrives after the scope changed belongs to the old scope and is dropped.
    const run = ++generation.current;
    setRefreshing(true);
    const site = scope.siteId;
    const results = await Promise.allSettled([
      operatorFetch<{ overview: OperatorOverview }>("/operator/overview"),
      operatorFetch<{ pipeline: OperatorPipelineV1 }>(`/operator/pipeline${site ? `?siteId=${site}` : ""}`),
      operatorFetch<{ recap: OperatorRecapV1 }>(`/operator/recap?period=${scope.period}${site ? `&siteId=${site}` : ""}`),
    ]);
    if (run !== generation.current) return;
    const [overviewResult, pipelineResult, recapResult] = results;
    if (overviewResult.status === "fulfilled") setOverview(overviewResult.value.overview);
    if (pipelineResult.status === "fulfilled") setPipeline(pipelineResult.value.pipeline);
    if (recapResult.status === "fulfilled") setRecap(recapResult.value.recap);

    const failures = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
    setAllFailed(failures.length === results.length);
    setAnyFailed(failures.length > 0);
    if (failures.length === 0) {
      setError("");
      setLastOkAt(Date.now());
    } else {
      const reason = failures[0]?.reason;
      setError(reason instanceof Error ? reason.message : "Mission Control could not load");
    }
    setRefreshing(false);
  }, [scope.siteId, scope.period]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, REFRESH_INTERVAL_MS);
    // Coming back to the tab refreshes at once instead of showing minutes-old numbers.
    const onVisible = () => { if (document.visibilityState === "visible") void load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const freshness = deriveFreshness({ hasData: overview !== null, refreshing, allFailed, anyFailed, lastOkAt, now });
  return {
    overview,
    pipeline,
    recap,
    error,
    clearError: () => setError(""),
    refreshing,
    lastOkAt,
    freshness,
    now,
    reload: load,
  };
}
